package one.healthium.uscreen.ui.blocks

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import one.healthium.uscreen.domain.models.HorizontalRulerBlock
import one.healthium.uscreen.domain.models.ImageBlock
import one.healthium.uscreen.domain.models.ProgressSliderBlock
import one.healthium.uscreen.domain.models.SpacerBlock
import one.healthium.uscreen.domain.models.StackBlock
import one.healthium.uscreen.domain.models.UScreen
import one.healthium.uscreen.domain.models.UScreenBlock
import one.healthium.uscreen.domain.models.VerticalRulerBlock
import one.healthium.uscreen.ui.UScreenAnswer
import one.healthium.uscreen.ui.UScreenCallbacks
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.ui.platform.LocalDensity
import one.healthium.uscreen.ui.layout.LocalCtaHeight
import one.healthium.uscreen.ui.layout.LocalProgressBarHeight
import one.healthium.uscreen.ui.animation.blockTransition
import one.healthium.uscreen.ui.layout.SubcomposeBlockLayout
import one.healthium.uscreen.ui.layout.CustomMeasureBlockLayout
import one.healthium.uscreen.ui.layout.flexSpacer

/**
 * UI-layer layout hint: block's composable uses fillMaxSize/weight
 * and needs remaining vertical space distributed to it.
 */
private val UScreenBlock.fillHeight: Boolean
    get() = this is VerticalRulerBlock || this is HorizontalRulerBlock

enum class BlockLayoutMode {
    SubcomposeLayout,
    CustomLayout,
}

private const val FLEX_FALLBACK_DP = 24
private const val SCROLL_EXTRA_BOTTOM_DP = 80

@Composable
fun UScreenBlockColumn(
    modifier: Modifier = Modifier,
    screen: UScreen,
    answers: Map<String, UScreenAnswer>,
    isMale: Boolean?,
    placeholders: Map<String, String>,
    hasProgressBar: Boolean,
    callbacks: UScreenCallbacks,
    layoutMode: BlockLayoutMode = BlockLayoutMode.CustomLayout,
    ctaHeightOverride: Dp? = null,
    screenId: String = "",
) {
    val density = LocalDensity.current
    val navBarHeight = with(density) { WindowInsets.navigationBars.getBottom(density).toDp() }
    val ctaHeight = ctaHeightOverride
        ?: if (screen.cta != null) LocalCtaHeight.current else navBarHeight
    val progressBarHeight = if (hasProgressBar) LocalProgressBarHeight.current else 0.dp

    when (layoutMode) {
        BlockLayoutMode.SubcomposeLayout -> SubcomposeBlockColumnContent(
            modifier = modifier,
            screen = screen,
            answers = answers,
            isMale = isMale,
            placeholders = placeholders,
            hasProgressBar = hasProgressBar,
            callbacks = callbacks,
            topPaddingDp = progressBarHeight.value.toInt(),
            ctaHeightDp = ctaHeight.value.toInt(),
            screenId = screenId,
        )
        BlockLayoutMode.CustomLayout -> CustomLayoutBlockColumnContent(
            modifier = modifier,
            screen = screen,
            answers = answers,
            isMale = isMale,
            placeholders = placeholders,
            hasProgressBar = hasProgressBar,
            callbacks = callbacks,
            topPaddingDp = progressBarHeight.value.toInt(),
            ctaHeightDp = ctaHeight.value.toInt(),
            screenId = screenId,
        )
    }
}

@Composable
private fun SubcomposeBlockColumnContent(
    modifier: Modifier,
    screen: UScreen,
    answers: Map<String, UScreenAnswer>,
    isMale: Boolean?,
    placeholders: Map<String, String>,
    hasProgressBar: Boolean,
    callbacks: UScreenCallbacks,
    topPaddingDp: Int,
    ctaHeightDp: Int,
    screenId: String = "",
) {
    SubcomposeBlockLayout(
        modifier = modifier.fillMaxSize().testTag("uscreen_block_column"),
        bottomPadding = ctaHeightDp,
    ) { scrollable, measureOnly ->
        // Top padding for progress bar overlay
        if (topPaddingDp > 0) {
            Spacer(Modifier.height(topPaddingDp.dp))
        }

        screen.blocks.forEachIndexed { index, block ->
            val blockModifier = if (index == 0 && !hasProgressBar && block !is ImageBlock && block !is StackBlock) {
                Modifier.statusBarsPadding()
            } else {
                Modifier
            }

            if (block is SpacerBlock && block.flex != null) {
                if (scrollable) {
                    Spacer(modifier = blockModifier.height(FLEX_FALLBACK_DP.dp))
                } else {
                    Spacer(modifier = blockModifier.weight(block.flex!!))
                }
            } else if (measureOnly && block is ProgressSliderBlock) {
                // Skip stateful blocks in measure pass to avoid double side effects
            } else {
                val fillModifier = if (!scrollable && block.fillHeight) {
                    blockModifier.weight(1f)
                } else {
                    blockModifier
                }
                val transitionModifier = if (!measureOnly) {
                    fillModifier.blockTransition(block, screenId)
                } else {
                    fillModifier
                }
                UScreenBlockRenderer(
                    modifier = transitionModifier,
                    block = block,
                    answers = answers,
                    isMale = isMale,
                    placeholders = placeholders,
                    callbacks = callbacks,
                    scrollable = scrollable,
                )
            }
        }

        // Bottom padding: CTA + extra on scrollable (skip in measure pass to not affect scroll decision)
        if (!measureOnly) {
            val bottomPadding = ctaHeightDp.dp + if (scrollable) SCROLL_EXTRA_BOTTOM_DP.dp else 0.dp
            if (bottomPadding > 0.dp) {
                Spacer(Modifier.height(bottomPadding))
            }
        }
    }
}

@Composable
private fun CustomLayoutBlockColumnContent(
    modifier: Modifier,
    screen: UScreen,
    answers: Map<String, UScreenAnswer>,
    isMale: Boolean?,
    placeholders: Map<String, String>,
    hasProgressBar: Boolean,
    callbacks: UScreenCallbacks,
    topPaddingDp: Int,
    ctaHeightDp: Int,
    screenId: String = "",
) {
    CustomMeasureBlockLayout(
        modifier = modifier.fillMaxSize().testTag("uscreen_block_column"),
        fallbackFlexDp = FLEX_FALLBACK_DP,
        bottomPadding = ctaHeightDp,
    ) {
        // Top padding for progress bar overlay
        if (topPaddingDp > 0) {
            Spacer(Modifier.height(topPaddingDp.dp))
        }

        screen.blocks.forEachIndexed { index, block ->
            val blockModifier = if (index == 0 && !hasProgressBar && block !is ImageBlock && block !is StackBlock) {
                Modifier.statusBarsPadding()
            } else {
                Modifier
            }

            if (block is SpacerBlock && block.flex != null) {
                Spacer(modifier = blockModifier.fillMaxWidth().flexSpacer(block.flex!!))
            } else {
                val flexModifier = if (block.fillHeight) {
                    blockModifier.fillMaxWidth().flexSpacer(1f)
                } else {
                    blockModifier.fillMaxWidth()
                }
                // Wrap in a Column to provide ColumnScope for BlockRenderer
                Column(modifier = flexModifier.blockTransition(block, screenId)) {
                    UScreenBlockRenderer(
                        modifier = Modifier,
                        block = block,
                        answers = answers,
                        isMale = isMale,
                        placeholders = placeholders,
                        callbacks = callbacks,
                        scrollable = false,
                    )
                }
            }
        }
    }
}
